/**
 * AdminSettings — Tableau de bord paramètres administrateur (premium UI).
 *
 * 3 sections consolidées dans un seul composant :
 *   1. API Management (clé custom + Universal Key + test connexion)
 *   2. Profil & Sécurité (changement mot de passe, email de récupération)
 *   3. Paramètres globaux (legacy : mode maintenance + table clé/valeur)
 *
 * Aucune nouvelle dépendance : Tailwind + shadcn/ui + lucide-react + sonner.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  KeyRound, ShieldCheck, Mail, Settings as SettingsIcon, Loader2,
  CheckCircle2, XCircle, Eye, EyeOff, RotateCcw, Sparkles, Lock,
  AlertTriangle, Zap, Save,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import {
  fetchProfileSettings,
  saveCustomApiKey,
  revertToUniversalKey,
  testApiKey,
  saveRecoveryEmail,
  changeAdminPassword,
  fetchSettings,
  patchSetting,
} from "@/lib/admin-api";

/* ============================================================== */
/* Utils                                                           */
/* ============================================================== */
function scorePassword(pw) {
  if (!pw) return { score: 0, label: "—", color: "bg-gray-200" };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  const classes =
    (/[a-z]/.test(pw) ? 1 : 0) +
    (/[A-Z]/.test(pw) ? 1 : 0) +
    (/\d/.test(pw) ? 1 : 0) +
    (/[^a-zA-Z0-9]/.test(pw) ? 1 : 0);
  score += Math.min(Math.floor(classes / 2), 2);
  score = Math.min(score, 4);
  const labels = ["Très faible", "Faible", "Correct", "Bon", "Excellent"];
  const colors = ["bg-red-500", "bg-orange-500", "bg-yellow-500", "bg-emerald-500", "bg-emerald-600"];
  return { score, label: labels[score], color: colors[score] };
}

/* ============================================================== */
/* Section 1 — API Management                                      */
/* ============================================================== */
function ApiManagementSection({ profile, onRefresh }) {
  const [customKey, setCustomKey] = useState("");
  const [revealStored, setRevealStored] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const api = profile?.api || {};
  const isCustom = api.active_source === "custom";

  async function handleSave() {
    if (!customKey || customKey.length < 8) {
      toast.error("Clé API trop courte (min. 8 caractères).");
      return;
    }
    setSaving(true);
    try {
      await saveCustomApiKey(customKey.trim());
      toast.success("Clé API personnalisée enregistrée.");
      setCustomKey("");
      setTestResult(null);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement.");
    } finally { setSaving(false); }
  }

  async function handleRevert() {
    if (!window.confirm("Revenir à la clé Universelle Emergent ? La clé personnalisée sera supprimée.")) return;
    setSaving(true);
    try {
      await revertToUniversalKey();
      toast.success("Restauration de la clé Universelle Emergent.");
      setTestResult(null);
      onRefresh();
    } catch {
      toast.error("Échec.");
    } finally { setSaving(false); }
  }

  async function handleTest(useTyped) {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await testApiKey(useTyped ? customKey.trim() : null);
      setTestResult(res);
      if (res?.success) toast.success(`Connexion OK · ${res.latency_ms} ms`);
      else toast.error(`Échec : ${res?.error || "erreur inconnue"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test impossible.");
    } finally { setTesting(false); }
  }

  return (
    <Card className="border-slate-200/70 shadow-sm transition hover:shadow-md dark:border-slate-700/60" data-testid="settings-api-card">
      <CardHeader className="border-b border-slate-100 pb-4 dark:border-slate-800">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 p-2.5 text-white shadow-sm">
            <KeyRound className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-lg font-semibold tracking-tight">API Management</CardTitle>
            <CardDescription className="mt-0.5 text-sm">
              Configurez votre clé personnalisée ou utilisez la clé universelle système par défaut.
            </CardDescription>
          </div>
          <Badge
            variant="outline"
            className={cn(
              "h-7 gap-1.5 px-2.5 font-medium",
              isCustom
                ? "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-700 dark:bg-indigo-950 dark:text-indigo-300"
                : "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
            )}
            data-testid="settings-api-active-badge"
          >
            <Sparkles className="h-3 w-3" />
            {isCustom ? "Custom Key" : "System Default"}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-5 p-5">
        {/* List item — System Default (Emergent Universal) */}
        <div
          className={cn(
            "flex items-center justify-between rounded-xl border p-4 transition",
            !isCustom
              ? "border-emerald-300 bg-emerald-50/50 dark:border-emerald-700 dark:bg-emerald-950/30"
              : "border-slate-200 bg-slate-50/50 dark:border-slate-700 dark:bg-slate-900/40"
          )}
          data-testid="settings-api-universal-row"
        >
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-white p-2 shadow-sm ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
              <Zap className="h-4 w-4 text-amber-500" />
            </div>
            <div>
              <div className="text-sm font-semibold">{api.system_default_label || "Emergent Universal Key"}</div>
              <div className="text-xs text-muted-foreground">
                Fallback automatique géré par la plateforme · Aucune configuration requise
              </div>
            </div>
          </div>
          <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">
            {api.system_default_available ? "Disponible" : "Indisponible"}
          </Badge>
        </div>

        {/* List item — Custom Key */}
        <div
          className={cn(
            "rounded-xl border p-4 transition",
            isCustom
              ? "border-indigo-300 bg-indigo-50/40 dark:border-indigo-700 dark:bg-indigo-950/30"
              : "border-slate-200 dark:border-slate-700"
          )}
          data-testid="settings-api-custom-row"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-white p-2 shadow-sm ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
                <KeyRound className="h-4 w-4 text-indigo-500" />
              </div>
              <div>
                <div className="text-sm font-semibold">Clé API personnalisée</div>
                <div className="text-xs text-muted-foreground">
                  Surcharge la clé système — utilisée prioritairement par tous les modules LLM.
                </div>
              </div>
            </div>
            {api.custom_key_set ? (
              <div className="flex items-center gap-2">
                <code className="rounded-md bg-slate-100 px-2 py-1 font-mono text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-300" data-testid="settings-api-custom-masked">
                  {revealStored ? api.custom_key_masked : "•••••••••••••••"}
                </code>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => setRevealStored(v => !v)}
                  data-testid="settings-api-reveal"
                >
                  {revealStored ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </Button>
              </div>
            ) : (
              <Badge variant="outline" className="text-[10px] uppercase">Non définie</Badge>
            )}
          </div>

          <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto_auto]">
            <Input
              type="password"
              placeholder="sk-... ou clé personnalisée"
              value={customKey}
              onChange={(e) => setCustomKey(e.target.value)}
              className="h-10 font-mono text-sm transition focus:ring-2 focus:ring-indigo-200"
              data-testid="settings-api-input"
              autoComplete="off"
            />
            <Button
              type="button"
              variant="outline"
              className="h-10 transition active:scale-[0.97]"
              onClick={() => handleTest(true)}
              disabled={testing || !customKey || customKey.length < 8}
              data-testid="settings-api-test"
            >
              {testing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Zap className="mr-1.5 h-4 w-4" />}
              Test Connection
            </Button>
            <Button
              type="button"
              className="h-10 bg-indigo-600 transition hover:bg-indigo-700 active:scale-[0.97]"
              onClick={handleSave}
              disabled={saving || !customKey || customKey.length < 8}
              data-testid="settings-api-save"
            >
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
              Enregistrer
            </Button>
          </div>

          {/* Test result indicator */}
          {testResult && (
            <div
              className={cn(
                "mt-3 flex items-start gap-2 rounded-lg border px-3 py-2 text-xs transition",
                testResult.success
                  ? "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200"
                  : "border-red-300 bg-red-50 text-red-900 dark:border-red-700 dark:bg-red-950/40 dark:text-red-200"
              )}
              data-testid="settings-api-test-result"
            >
              {testResult.success ? (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              ) : (
                <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
              )}
              <div className="flex-1">
                <div className="font-medium">
                  {testResult.success ? "Connexion réussie" : "Échec de la connexion"}
                  {typeof testResult.latency_ms === "number" && (
                    <span className="ml-2 font-mono text-[10px] opacity-70">{testResult.latency_ms} ms</span>
                  )}
                </div>
                {!testResult.success && testResult.error && (
                  <div className="mt-0.5 font-mono text-[10px] opacity-80">{testResult.error}</div>
                )}
                <div className="mt-0.5 text-[10px] opacity-70">
                  Source testée : <span className="font-medium">{testResult.source}</span>
                </div>
              </div>
            </div>
          )}

          {/* Bottom controls */}
          <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3 dark:border-slate-800">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={() => handleTest(false)}
              disabled={testing}
              data-testid="settings-api-test-active"
            >
              {testing ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Zap className="mr-1 h-3 w-3" />}
              Tester la source active
            </Button>
            {api.custom_key_set && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-xs text-amber-700 hover:bg-amber-50 hover:text-amber-800 dark:text-amber-400"
                onClick={handleRevert}
                disabled={saving}
                data-testid="settings-api-revert"
              >
                <RotateCcw className="mr-1 h-3 w-3" />
                Revenir à la clé Universelle
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ============================================================== */
/* Section 2 — Admin Security & Profile                            */
/* ============================================================== */
function SecurityProfileSection({ profile, onRefresh }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);

  const [recoveryEmail, setRecoveryEmail] = useState(profile?.recovery_email || "");
  const [emailBusy, setEmailBusy] = useState(false);

  useEffect(() => {
    setRecoveryEmail(profile?.recovery_email || "");
  }, [profile?.recovery_email]);

  const strength = useMemo(() => scorePassword(next), [next]);
  const match = next.length > 0 && next === confirm;

  async function handleChangePassword(e) {
    e.preventDefault();
    if (!current || !next) { toast.error("Tous les champs sont requis."); return; }
    if (next !== confirm) { toast.error("La confirmation ne correspond pas."); return; }
    if (strength.score < 3) { toast.error("Mot de passe trop faible."); return; }
    setPwBusy(true);
    try {
      await changeAdminPassword(current, next);
      toast.success("Mot de passe mis à jour avec succès.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du changement.");
    } finally { setPwBusy(false); }
  }

  async function handleSaveRecoveryEmail() {
    if (!recoveryEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recoveryEmail)) {
      toast.error("Email invalide."); return;
    }
    setEmailBusy(true);
    try {
      await saveRecoveryEmail(recoveryEmail.trim().toLowerCase());
      toast.success("Email de récupération mis à jour.");
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec.");
    } finally { setEmailBusy(false); }
  }

  return (
    <Card className="border-slate-200/70 shadow-sm transition hover:shadow-md dark:border-slate-700/60" data-testid="settings-security-card">
      <CardHeader className="border-b border-slate-100 pb-4 dark:border-slate-800">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-gradient-to-br from-red-500 to-rose-600 p-2.5 text-white shadow-sm">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-lg font-semibold tracking-tight">Sécurité & Profil administrateur</CardTitle>
            <CardDescription className="mt-0.5 text-sm">
              Compte cible :
              <code className="ml-1.5 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-300" data-testid="settings-admin-email">
                {profile?.admin_email || "—"}
              </code>
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="grid grid-cols-1 gap-6 p-5 lg:grid-cols-2">
        {/* Change Password */}
        <form onSubmit={handleChangePassword} className="space-y-3" data-testid="settings-password-form">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-slate-500" />
            <h3 className="text-sm font-semibold">Changer le mot de passe</h3>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="current-pw" className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Mot de passe actuel
            </Label>
            <Input
              id="current-pw"
              type={showPw ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="h-10 transition focus:ring-2 focus:ring-red-200"
              autoComplete="current-password"
              data-testid="settings-current-password"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-pw" className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Nouveau mot de passe
            </Label>
            <div className="relative">
              <Input
                id="new-pw"
                type={showPw ? "text" : "password"}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                className="h-10 pr-10 transition focus:ring-2 focus:ring-red-200"
                autoComplete="new-password"
                data-testid="settings-new-password"
              />
              <button
                type="button"
                onClick={() => setShowPw(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-500 transition hover:bg-slate-100 dark:hover:bg-slate-800"
                aria-label="Afficher/masquer le mot de passe"
                data-testid="settings-toggle-password-visibility"
              >
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {next.length > 0 && (
              <div className="space-y-1 pt-1" data-testid="settings-password-strength">
                <div className="flex gap-1">
                  {[0, 1, 2, 3].map(i => (
                    <div
                      key={i}
                      className={cn(
                        "h-1 flex-1 rounded-full transition-colors duration-300",
                        i < strength.score ? strength.color : "bg-slate-200 dark:bg-slate-800"
                      )}
                    />
                  ))}
                </div>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="font-medium text-slate-600 dark:text-slate-400">{strength.label}</span>
                  <span className="text-slate-400">8+ car. · majuscule · chiffre · symbole</span>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirm-pw" className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Confirmer le nouveau mot de passe
            </Label>
            <Input
              id="confirm-pw"
              type={showPw ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={cn(
                "h-10 transition focus:ring-2",
                confirm.length === 0
                  ? "focus:ring-red-200"
                  : match
                  ? "border-emerald-400 focus:ring-emerald-200"
                  : "border-red-400 focus:ring-red-200"
              )}
              autoComplete="new-password"
              data-testid="settings-confirm-password"
            />
            {confirm.length > 0 && !match && (
              <p className="flex items-center gap-1 text-[11px] text-red-600">
                <AlertTriangle className="h-3 w-3" /> Les mots de passe ne correspondent pas.
              </p>
            )}
          </div>

          <Button
            type="submit"
            className="h-10 w-full bg-red-700 transition hover:bg-red-800 active:scale-[0.98]"
            disabled={pwBusy || !current || !next || !match || strength.score < 3}
            data-testid="settings-submit-password"
          >
            {pwBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
            Mettre à jour le mot de passe
          </Button>
        </form>

        {/* Recovery Email */}
        <div className="space-y-3" data-testid="settings-recovery-section">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-slate-500" />
            <h3 className="text-sm font-semibold">Email de récupération secondaire</h3>
          </div>
          <p className="text-xs leading-relaxed text-muted-foreground">
            Cet email est utilisé par le flux « Mot de passe oublié » pour envoyer le lien de
            réinitialisation. Conservez-le sur un domaine de messagerie séparé de votre compte principal.
          </p>

          <div className="space-y-1.5">
            <Label htmlFor="recovery-email" className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Adresse email
            </Label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                id="recovery-email"
                type="email"
                value={recoveryEmail}
                onChange={(e) => setRecoveryEmail(e.target.value)}
                placeholder="apostrophe-moi@gmail.com"
                className="h-10 pl-9 font-mono text-sm transition focus:ring-2 focus:ring-red-200"
                data-testid="settings-recovery-email-input"
              />
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="h-10 w-full transition active:scale-[0.98]"
            onClick={handleSaveRecoveryEmail}
            disabled={emailBusy || !recoveryEmail || recoveryEmail === profile?.recovery_email}
            data-testid="settings-save-recovery-email"
          >
            {emailBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Enregistrer l'email de récupération
          </Button>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[11px] leading-relaxed text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
            <div className="flex items-start gap-1.5">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                <strong>RGPD :</strong> toute modification est journalisée dans l'audit log (180 jours)
                et copiée dans l'archive critique (3 ans).
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ============================================================== */
/* Section 3 — Global App Settings (preserved legacy)              */
/* ============================================================== */
function GlobalSettingsSection() {
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(null);

  async function load() {
    try { setRows(await fetchSettings()); }
    catch { /* silent */ }
  }
  useEffect(() => { load(); }, []);

  const coerce = (v) => {
    if (typeof v === "boolean") return v;
    if (typeof v !== "string") return v;
    if (v === "true") return true;
    if (v === "false") return false;
    if (!isNaN(Number(v)) && v.trim() !== "") return Number(v);
    return v;
  };

  async function save(key) {
    const val = key in edits ? edits[key] : rows.find(r => r.key === key)?.value;
    setSaving(key);
    try {
      await patchSetting(key, coerce(val));
      toast.success(`« ${key} » mis à jour.`);
      setEdits(e => { const c = { ...e }; delete c[key]; return c; });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec.");
    } finally { setSaving(null); }
  }

  const maintenance = rows.find(r => r.key === "maintenance_mode");
  // Hide API/profile-related keys (gérés par les sections dédiées)
  const visibleRows = rows.filter(r => !["custom_llm_api_key", "recovery_email"].includes(r.key));

  return (
    <Card className="border-slate-200/70 shadow-sm transition hover:shadow-md dark:border-slate-700/60" data-testid="settings-global-card">
      <CardHeader className="border-b border-slate-100 pb-4 dark:border-slate-800">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-gradient-to-br from-slate-600 to-slate-800 p-2.5 text-white shadow-sm">
            <SettingsIcon className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-lg font-semibold tracking-tight">Paramètres globaux de l'application</CardTitle>
            <CardDescription className="mt-0.5 text-sm">
              Maintenance, quotas et limites système — réservé aux super-admins.
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 p-5">
        {maintenance && (
          <div
            className={cn(
              "flex items-center justify-between gap-4 rounded-xl border p-4 transition",
              maintenance.value
                ? "border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/40"
                : "border-slate-200 bg-slate-50/40 dark:border-slate-700"
            )}
          >
            <div>
              <div className="text-sm font-semibold">Mode maintenance</div>
              <div className="text-xs text-muted-foreground">
                Quand activé, les routes <code className="rounded bg-white px-1 dark:bg-slate-800">/api</code> sauf <code className="rounded bg-white px-1 dark:bg-slate-800">/admin</code> renvoient 503.
              </div>
            </div>
            <Switch
              checked={Boolean(maintenance.value)}
              onCheckedChange={async (checked) => {
                if (checked && !window.confirm("Activer la maintenance ? Les utilisateurs seront bloqués.")) return;
                await patchSetting("maintenance_mode", checked);
                toast.success(checked ? "Maintenance ACTIVÉE" : "Maintenance DÉSACTIVÉE");
                load();
              }}
              data-testid="settings-maintenance-toggle"
            />
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900/40">
              <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2.5">Clé</th>
                <th className="px-3 py-2.5">Valeur</th>
                <th className="px-3 py-2.5">Description</th>
                <th className="w-28 px-3 py-2.5">Action</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.filter(r => r.key !== "maintenance_mode").map((r) => (
                <tr key={r.key} className="border-t border-slate-100 transition hover:bg-slate-50/60 dark:border-slate-800 dark:hover:bg-slate-900/40">
                  <td className="px-3 py-2 font-mono text-xs">{r.key}</td>
                  <td className="px-3 py-2">
                    <Input
                      className="h-8 transition focus:ring-2 focus:ring-slate-300"
                      value={edits[r.key] !== undefined ? edits[r.key] : String(r.value)}
                      onChange={e => setEdits(x => ({ ...x, [r.key]: e.target.value }))}
                      data-testid={`global-setting-input-${r.key}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{r.description}</td>
                  <td className="px-3 py-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 transition active:scale-95"
                      disabled={saving === r.key}
                      onClick={() => save(r.key)}
                      data-testid={`global-setting-save-${r.key}`}
                    >
                      {saving === r.key ? <Loader2 className="h-3 w-3 animate-spin" /> : "Enregistrer"}
                    </Button>
                  </td>
                </tr>
              ))}
              {visibleRows.length === 0 && (
                <tr><td colSpan={4} className="px-3 py-6 text-center text-xs text-muted-foreground">Aucun paramètre.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

/* ============================================================== */
/* AdminSettings — Page                                            */
/* ============================================================== */
export default function AdminSettings() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    try {
      const data = await fetchProfileSettings();
      setProfile(data);
    } catch (e) {
      toast.error("Impossible de charger les paramètres.");
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="admin-settings-page">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <SettingsIcon className="h-6 w-6 text-slate-600 dark:text-slate-300" />
          <h1 className="text-2xl font-bold tracking-tight">Paramètres</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Gérez vos clés API, votre profil de sécurité et la configuration système globale.
        </p>
      </header>

      {loading ? (
        <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Chargement…
        </div>
      ) : (
        <>
          <ApiManagementSection profile={profile} onRefresh={refresh} />
          <SecurityProfileSection profile={profile} onRefresh={refresh} />
          <GlobalSettingsSection />
        </>
      )}
    </div>
  );
}
